import { useEffect, useRef, type ReactNode } from 'react';

export interface FormDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** onCancel: 취소 버튼 / Esc 키 / backdrop 클릭 시 호출. 미지정 시 onClose 로 fallback */
  onCancel?: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  /** data-testid for the submit button (E2E 셀렉터용) */
  submitTestId?: string;
  /** true 면 제출 버튼을 숨긴다(읽기 전용 탭 등 — cancel 만으로 닫기 · queue #684) */
  hideSubmit?: boolean;
  children: ReactNode;
}

export function FormDialog({
  open,
  title,
  onClose,
  onCancel,
  onSubmit,
  submitLabel = '저장',
  cancelLabel = '취소',
  submitTestId,
  hideSubmit = false,
  children,
}: FormDialogProps) {
  const handleCancel = onCancel ?? onClose;
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  // Escape 닫기 (R7-049)
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleCancel();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleCancel]);

  // 열릴 때 첫 포커스 이동 — 취소 버튼으로
  useEffect(() => {
    if (open) {
      // rAF 로 DOM 렌더 후 focus
      const id = requestAnimationFrame(() => cancelBtnRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      ref={dialogRef}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={handleCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          minWidth: '480px',
          maxWidth: '720px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>{title}</h2>
        <div style={{ marginTop: 'var(--spacing-4)' }}>{children}</div>
        <div
          style={{
            marginTop: 'var(--spacing-6)',
            display: 'flex',
            gap: 'var(--spacing-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={handleCancel}
          >
            {cancelLabel}
          </button>
          {!hideSubmit && (
            <button
              type="button"
              data-testid={submitTestId}
              onClick={onSubmit}
              style={{
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                padding: 'var(--spacing-2) var(--spacing-4)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
              }}
            >
              {submitLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
